import getCharts from '../getCharts'
import { PatientNoteData } from '../../constants/api.config'

/**
 * Optimized PatientNoteData API wrapper with:
 * - In-flight request deduplication (prevents concurrent duplicate requests)
 * - Persistent memory cache with TTL (reduces redundant API calls)
 * - Request coalescing (merges rapid requests for same patient)
 * - Smart cache invalidation (clears on mutations only)
 */

// In-flight requests: prevents simultaneous duplicate requests
const inFlightGetNotes = {}

// Persistent cache: { cacheKey: { data, timestamp } }
const persistentNoteCache = {}
const CACHE_TTL_MS = 5 * 60 * 1000 // 5 minutes

/**
 * Get cache key for patient
 */
const getCacheKey = (patientID) => String(patientID)

/**
 * Check if cached data is still valid (not expired)
 */
const isCacheValid = (cacheEntry) => {
  if (!cacheEntry) return false
  return Date.now() - cacheEntry.timestamp < CACHE_TTL_MS
}

/**
 * Invalidate specific cache entries when data is mutated
 */
const invalidateCache = (patientID) => {
  if (patientID) {
    delete persistentNoteCache[getCacheKey(patientID)]
  } else {
    // Full cache clear if no patient specified
    Object.keys(persistentNoteCache).forEach(key => delete persistentNoteCache[key])
  }
}

export default async function PatientNoteDataCRUD(mode, patientID = "-1", id = "-1", data = "", newDate = "", status = 'Draft', flag = 'false', cacheTTL = CACHE_TTL_MS, Services = ""
 ) {
    try {
        const payload = { mode, patientID, id, data, newDate, status, flag, Services }

        if (mode === 'GET' && patientID && patientID !== "-1") {
            const cacheKey = getCacheKey(patientID)
            
            // Check persistent cache first
            const cachedEntry = persistentNoteCache[cacheKey]
            if (isCacheValid(cachedEntry)) {
                return cachedEntry.data
            }

            // Check if request is already in-flight
            if (inFlightGetNotes[cacheKey]) {
                return inFlightGetNotes[cacheKey]
            }

            // Make new request and cache result
            const promise = (async () => {
                try {
                    const res = await getCharts(PatientNoteData, payload)
                    const data = res.data
                    
                    // Store in persistent cache
                    persistentNoteCache[cacheKey] = {
                        data,
                        timestamp: Date.now()
                    }
                    
                    return data
                } catch (error) {
                    // Keep cache on error (stale-while-error pattern)
                    if (cachedEntry) {
                        return cachedEntry.data
                    }
                    return error
                } finally {
                    // Remove from in-flight after completion
                    delete inFlightGetNotes[cacheKey]
                }
            })()

            inFlightGetNotes[cacheKey] = promise
            return promise
        }

        // For mutations, invalidate affected caches so follow-up GETs never reuse stale note lists.
        if ((mode === 'INSERT' || mode === 'UPDATE' || mode === 'DELETE' || mode === 'CREATE') && patientID && patientID !== "-1") {
            invalidateCache(patientID)
        }

        const res = await getCharts(PatientNoteData, payload)
        return res.data
    } catch (error) {
        return error
    }
}

/**
 * Utility function to manually clear cache for specific patient or all patients
 * Usage: PatientNoteDataCRUD.clearCache(patientID) or PatientNoteDataCRUD.clearCache()
 */
export function clearNoteCache(patientID) {
    invalidateCache(patientID)
}
